import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as CryptoJS from 'crypto-js';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { CharacterLimits, PdfType } from '../enum/assignment';
import { EncryptionType } from '../enum/encryption-type.enum';
import { ENVIRONMENT_URL, FrontendSiteType } from '../enum/environment_urls.enum';
import { ROLEVALUES, UserRoles } from '../enum/roles.enum';
import { ToasterMessages } from '../enum/toaster-messages.enum';
import { User } from '../interface/user';
import { MatDialogComponent } from '../shared/mat-dialog/mat-dialog.component';
import { RenamePdfDialogComponent } from '../shared/rename-pdf-dialog/rename-pdf-dialog.component';
import { OpenAICallRestriction, OpenAICredits, SubscriptionPlanTypes } from './../enum/openai_restrictions';
import { AICreditPointsService } from './aicredit-points.service';
import { AuthService } from './auth.service';
import { CalendarService } from './calendar.service';
import { HttpService } from './http.service';
import { SessionService } from './session-service.service';
import { StorageService } from './storage.service';
import { ToasterService } from './toaster.service';
declare var MathJax: any;

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  public _pageNumber: number = 1; // Default to page 1
  public _questionAsked: boolean = false;
  public scrapeDataSubject = new Subject<any>();
  public isSchoolListShowed = new Subject<any>();
  private selctedSchoolSource = new BehaviorSubject<any>(this.getStoredSchool()); // Holds last emitted value
  selctedSchoolFromList = this.selctedSchoolSource.asObservable(); // Expose as Observable
  private selctedSchoolFromCaasppSource = new BehaviorSubject<any>(null);
  public selctedSchoolFromCaaspp = this.selctedSchoolFromCaasppSource.asObservable();
  latLongOfUser = {};
  // Observable for loading state
  private loadingStateSubject = new BehaviorSubject<boolean>(false);
  loadingState = this.loadingStateSubject.asObservable();

  constructor(
    private sanitizer: DomSanitizer,
    private httpService: HttpService,
    private authService: AuthService,
    private storageService: StorageService,
    private creditPointService: AICreditPointsService,
    private dialog: MatDialog,
    private toasterService: ToasterService,
    public spinnerService: NgxSpinnerService,
    private calendarService: CalendarService,
    private sessionService: SessionService,
  ) {}

  // Method to show the loader
  showLoading() {
    this.loadingStateSubject.next(true);
  }

  // Method to hide the loader
  hideLoading() {
    this.loadingStateSubject.next(false);
  }

  selctedSchoolData(schoolData: any) {
    this.selctedSchoolSource.next(schoolData);
    localStorage.setItem('selectedSchoolFromList', JSON.stringify(schoolData));
  }

  private getStoredSchool(): any {
    const storedData = localStorage.getItem('selectedSchoolFromList');
    return storedData ? JSON.parse(storedData) : null;
  }

  selctedSchoolScrapeData(scarpeData: any) {
    this.selctedSchoolFromCaasppSource.next(scarpeData);
  }

  sendScrapeData(scarpeData) {
    this.scrapeDataSubject.next(scarpeData);
  }

  sendSchoolList(showSchoolList) {
    this.isSchoolListShowed.next(showSchoolList);
  }

  // Getter for admin-asked question in admin assistant in a small view.
  get questionAsked(): boolean {
    return this._questionAsked;
  }
  // Setter for admin-asked question in admin assistant in a expanded view.
  set questionAsked(value: boolean) {
    this._questionAsked = value;
  }
  // Getter for page number
  get pageNumber(): number {
    return this._pageNumber;
  }

  // Setter for page number
  set pageNumber(value: number) {
    if (value > 0) {
      this._pageNumber = value;
    } else {
      console.warn('Page number must be greater than 0');
    }
  }

  randomString(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  getFileSize(bytes) {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let l = 0,
      n = parseInt(bytes, 10) || 0;

    // Handle zero or negative file size
    if (n <= 0) {
      return '0 bytes';
    }

    // Iteratively divide the file size by 1024 until it's less than 1024, keeping track of the unit index
    const maxIterations = units.length - 1;

    while (n >= 1024 && l < maxIterations) {
      n = n / 1024;
      l++;
    }

    return n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l];
  }
  getFileTypeName(isExtension = true, fileName: string) {
    if (isExtension) {
      return fileName.split('.').pop();
    } else {
      return fileName.split('.')[0];
    }
  }

  getSafeUrl(url: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getStatusClass(value: number): string {
    try {
      if (value <= 20) {
        return 'danger';
      } else if (value <= 40) {
        return 'warning';
      } else {
        return 'success';
      }
    } catch (error) {}
  }
  getMediaMimeType(type: string): string {
    if (type === 'video') {
      return 'video/*';
    } else if (type === 'audio') {
      return 'audio/*';
    } else if (type === 'pdf') {
      return 'application/pdf';
    } else {
      return 'image/*';
    }
  }

  /**
   * @description Get safe video URL for embedding or normal viewing
   * @param url
   * @param isSafe
   * @returns SafeResourceUrl
   */
  getSafeVideoUrl(url: string, isSafe = true): SafeResourceUrl {
    if (isSafe) {
      const newUrl = url.replace('watch?v=', 'embed/');
      return this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
    } else {
      const newUrl = url.replace('embed/', 'watch?v=');
      return newUrl;
    }
  }

  getWeekDayString(day: number): string {
    switch (day) {
      case 0:
        return 'Sunday';
      case 1:
        return 'Monday';
      case 2:
        return 'Tuesday';
      case 3:
        return 'Wednesday';
      case 4:
        return 'Thursday';
      case 5:
        return 'Friday';
      case 6:
        return 'Saturday';
    }
  }

  async validateDocumentNumber(documentNumber: string): Promise<any> {
    if (!documentNumber) {
      throw new Error('Document number is required.');
    }
    const params = new HttpParams().set('documentNumber', documentNumber);

    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.NODE_SERVER}${EndPoint.validate_document_number}`, { params }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getLcapSchoolData(searchSchoolData: any): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.NODE_SERVER}${EndPoint.lcap_school_data}`, searchSchoolData),
      );

      return response;
    } catch (error) {
      console.error('Error during HTTP request:', error);
      throw error;
    }
  }

  async scrapeCDSDashboaredBySchool(schoolName, cdsCode, schoolYear) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(
          `${environment.NODE_SERVER}${EndPoint.lcap_scrape}?schoolName=${schoolName}&cdsCode=${cdsCode}&year=${schoolYear}`,
        ),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // user tokens

  async getUserToken(user: User) {
    try {
      const params = {
        user_id: user.id,
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime(),
        end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime(),
      };

      const response = await this.authService.getUserAccessToken(params);
      return response;
    } catch (error) {
      console.log('util service getUserToken', error);
    }
  }

  async updateTokenUsed(user: User, userAccessToken?: any) {
    try {
      const body = {
        user_id: user?.id,
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime(),
        end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime(),

        tokens_used: userAccessToken[0]?.tokens_used ? userAccessToken[0]?.tokens_used + 1 : 1,
      };
      if (userAccessToken[0]?.id) body['tokens_used_id'] = userAccessToken[0]?.id;

      await this.authService.updateUserAccessToken(body);

      // await this.getUserToken(user);
    } catch (error) {
      console.log('util service updateTokenUsed', error);
    }
  }

  getFormattedAnswers(answer: any): string {
    if (!answer) return '';

    // Extract value from FormGroup if present
    const answerText =
      answer instanceof FormGroup ? answer.value?.option?.replace(/\\\(|\\\)|\*\*/g, '').trim() : answer;

    return typeof answerText === 'string' ? answerText.replace(/\*\*/g, '').replace(/\$/g, '\\$').trim() : '';
  }

  async youTubeUrls(videoUrls: string[]) {
    try {
      const urls = [];
      // display the correct response instead of a random url.
      videoUrls.map((videoUrl) => {
        if (videoUrl.includes('www.youtube.com') || videoUrl.includes('www.youtube-nocookie.com')) {
          urls.push(this.getSafeVideoUrl(videoUrl));
        }
      });
      return urls;
    } catch (error) {
      // console.log(error);
    }
  }

  getFormattedAnswerWithString(answerValue: any, isTextarea: boolean): string {
    if (!answerValue) return '';

    // Ensure answerValue is a string
    const answerText =
      typeof answerValue === 'string'
        ? answerValue
        : Array.isArray(answerValue)
          ? answerValue[0]
          : JSON.stringify(answerValue);

    // If used in a textarea, keep newlines; otherwise, convert to <br>
    return isTextarea
      ? answerText.replace(/\*\*/g, '').replace(/\n/g, '').replace(/#+/g, '').replace(/\$/g, '\\$').trim()
      : answerText
          .replace(/\*\*/g, '')
          .replace(/\n/g, '<br>')
          .replace(/^<br>/, '')
          .replace(/<br>$/, '')
          .replace(/#+/g, '')
          .replace(/\$/g, '\\$')
          .trim();
  }

  async getCAASPPSchoolInfo(searchSchoolData: any): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.caaspp_school_list}`, {
          params: searchSchoolData,
        }),
      );

      return response;
    } catch (error) {
      console.error('Error during HTTP request:', error);
      throw error;
    }
  }

  async scrapeCaasppBySchool(params) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.NODE_SERVER}${EndPoint.caaspp_scrape}`, {
          params,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async scrapeCaaspAlternateEnglish(params) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.NODE_SERVER}${EndPoint.caaspp_scrape_alternate_english}`, {
          params,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async scrapeCaaspScience(params) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.NODE_SERVER}${EndPoint.caaspp_scrape_science}`, {
          params,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async scrapeInitialELPAC(params) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.NODE_SERVER}${EndPoint.elpac_scrape}`, {
          params,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  getUserLocation(): Observable<any> {
    return this.httpService.get(`${environment.LOCATION_WITHOUT_PERMISSION}`);
  }

  getRequestPermission(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getUserLocation().subscribe({
        next: (response) => {
          const { country_name, region } = response;
          this.latLongOfUser = { country_name, region };
          resolve(this.latLongOfUser);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  async updateTeacherLoginStatus(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.update_bulk_teachers_login_status}`,
          queryParams,
        ),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  //when want to access location permission
  //   getUserLocationByLatLong(lat: number, lon: number): Observable<any> {
  //     return this.httpService.get(`${environment.LOCATION_API}&lat=${lat}&lon=${lon}`);
  //   }

  // getRequestPermission(): Promise<any> {
  //   return new Promise((resolve, reject) => {
  //     const user = this.storageService.getItem('user');
  //     if (navigator.geolocation) {
  //       navigator.geolocation.getCurrentPosition(
  //         async (position) => {
  //           const latitude = position.coords.latitude;
  //           const longitude = position.coords.longitude;
  //           const response = await lastValueFrom(this.getUserLocationByLatLong(latitude, longitude));
  //           const { country, state } = response.address;
  //           this.latLongOfUser = { country, state };
  //           resolve(this.latLongOfUser);
  //         },

  //         (error) => {
  //           this.getUserLocation().subscribe({
  //             next: (response) => {
  //               const { country_name, region } = response;
  //               this.latLongOfUser = { country_name, region };
  //               resolve(this.latLongOfUser);
  //             },
  //           });
  //         },
  //       );
  //     } else {
  //       console.warn('Geolocation is not supported by this browser.');
  //     }
  //   });
  // }
  /**
   * Encrypts or decrypts a given text using AES-CBC with a backend-provided key.
   * Key is fetched once from backend and cached — never stored in frontend env variables.
   * @param {string} type - Operation type: 'encrypt' or 'decrypt'
   * @param {string} text - Plain text to encrypt or hex-encoded cipher text to decrypt
   * @returns {Promise<string>} Encrypted hex string or decrypted plain text
   */
  async encryptDecryptText(type: EncryptionType, text: string): Promise<string> {
    try {
      // Fetch key from backend instead of env variable
      const keyString = await this.sessionService.getEncryptionKey(); // ← replaces environment.JWT_KEY
      const key = CryptoJS.enc.Utf8.parse(keyString);
      const iv = CryptoJS.enc.Utf8.parse(keyString.substring(0, 16));

      if (type === EncryptionType.Encrypt) {
        const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(text), key, {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });
        return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
      }

      if (type === EncryptionType.Decrypt) {
        const encryptedHex = CryptoJS.enc.Hex.parse(text);
        const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext: encryptedHex });

        const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });

        const result = decrypted.toString(CryptoJS.enc.Utf8);
        if (!result) {
          throw new Error('Decryption produced empty string — possibly incorrect key or malformed input');
        }
        return result;
      }

      throw new Error('Invalid type. Use "encrypt" or "decrypt".');
    } catch (error) {
      console.error('encryptDecryptText error:', error);
      return '';
    }
  }

  //AI credit points

  async getUserAITokens(userId) {
    const userSubscription = await this.creditPointService.getSubscriptionByUserId({ user_id: userId });
    const response: any = await this.creditPointService.getAICreditByUserId({ user_id: userId });

    if (response && response.id) {
      return response;
    } else {
      const startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      const endDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime();

      const isActive = userSubscription?.subscription_status === 'active';
      const isMonthlyPlan = userSubscription?.plan_type === SubscriptionPlanTypes.MONTHLY;
      const isYearlyPlan = userSubscription?.plan_type === SubscriptionPlanTypes.YEARLY;
      const activeSubscription = (isMonthlyPlan || isYearlyPlan) && isActive;
      const creditPoints = activeSubscription ? OpenAICredits.MONTHLY : 0;

      const body = {
        user_id: userId,
        free_tokens: 15,
        tokens_used: 0,
        start_date: startDate,
        end_date: endDate,
        monthlyCreditPoints: isMonthlyPlan && isActive,
        yearlyCreditPoints: isYearlyPlan && isActive,
        total_ai_credits: creditPoints,
        referral_ai_credits: 0,
        subscription_ai_credits: creditPoints,
        sign_up_users: [],
        is_unlimited: false,
      };

      const userResponse: any = await this.creditPointService.addCreditPoints(body);
      if (userResponse && userResponse.id) {
        return userResponse;
      }
    }
  }

  async updateUserAITokens(userId, token) {
    try {
      const isMonthlyPlan = token.subscription?.plan_type === SubscriptionPlanTypes.MONTHLY;
      const isYearlyPlan = token.subscription?.plan_type === SubscriptionPlanTypes.YEARLY;
      const isSubscriptionActive = token?.subscription?.subscription_status === 'active';
      const startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      const endDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime();

      const monthlyCreditPoints = isMonthlyPlan && !isSubscriptionActive ? false : token.monthlyCreditPoints;
      const yearlyCreditPoints = isYearlyPlan && !isSubscriptionActive ? false : token.yearlyCreditPoints;
      const body: any = {
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
        monthlyCreditPoints,
        yearlyCreditPoints,
        tokens_used: token.tokens_used + 1,
        sign_up_users: token.sign_up_users,
        is_unlimited: token.is_unlimited ? token.is_unlimited : false,
      };

      if (token.free_tokens === 0) {
        if (token.referral_ai_credits >= 0) {
          body.free_tokens = 0;
          if (token.referral_ai_credits > 0) {
            body.referral_ai_credits = token.referral_ai_credits - 1;
            body.subscription_ai_credits = token.subscription_ai_credits ? token.subscription_ai_credits : 0;
          } else {
            body.referral_ai_credits = token.referral_ai_credits ? token.referral_ai_credits : 0;
            body.subscription_ai_credits = token.subscription_ai_credits ? token.subscription_ai_credits - 1 : 0;
          }
        }
      } else {
        body.free_tokens = token.free_tokens > 0 ? token.free_tokens - 1 : 0;
        body.referral_ai_credits = token.referral_ai_credits ? token.referral_ai_credits : 0;
        body.subscription_ai_credits = token.subscription_ai_credits ? token.subscription_ai_credits : 0;
      }
      await this.creditPointService.editCreditPointsRecord(body, userId);
    } catch (e) {
      // console.log('eeeee', e);
    }
  }
  checkIfTokenLimitExceededAndCreditsOut(userAccessAIToken: any, user: any) {
    const isTokenLimitExceeded =
      userAccessAIToken?.tokens_used &&
      userAccessAIToken?.tokens_used >=
        (this.storageService.checkUserRole(user?.role_id, UserRoles.TeacherOnly)
          ? OpenAICallRestriction.TEACHER_ONLY
          : OpenAICallRestriction.TRIAL_TEACHER);
    const allCreditPoints = userAccessAIToken?.referral_ai_credits + userAccessAIToken?.subscription_ai_credits;
    const isOutOfCredits = allCreditPoints === 0;
    const isSubscriptionActive = userAccessAIToken?.subscription?.subscription_status === 'active';
    if (isTokenLimitExceeded && isOutOfCredits && isSubscriptionActive && !userAccessAIToken?.is_unlimited) {
      this.showModalForNoCredits();
      this.spinnerService.hide();
      return true;
    } else if (isTokenLimitExceeded && isOutOfCredits && !userAccessAIToken?.is_unlimited) {
      this.toasterService.showToast(
        'You have exceeded token limit usage per month, please try again after a month',
        'danger',
      );
      this.spinnerService.hide();
      return true;
    }
    return false;
  }

  showModalForNoCredits() {
    this.dialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: `Welcome`,
        icon: `/assets/icons/featured icon.svg`,
        accessCodeLabel: `Your AI Credits Are Up! Share your referral code or subscribe to premium to boost your experience!`,
        confirmButtonText: 'Ok',
      },
    });
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  trimAndLimitToDigits(control: AbstractControl | null, maxLength: number = 14) {
    if (!control) return;

    const onlyDigits = control.value?.replace(/\D/g, '') || '';
    const trimmed = onlyDigits.substring(0, maxLength);

    if (control.value !== trimmed) {
      control.setValue(trimmed, { emitEvent: false });
      return control;
    }
  }

  // Format text to HTML with strong tags for double asterisks
  formatText(text: string): string {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }

  /**
   * @description
   * Checks and updates the user's login location.
   * - Skips location update if the user role is SUPER_ADMIN.
   * - Retrieves the user's current location (country & region).
   * - Updates the user record in the backend with the last login location.
   * - Syncs the updated user data into local storage.
   */
  async checkPermission() {
    try {
      const currentUser = this.storageService.getItem('user');
      if (!currentUser || !currentUser.user) {
        console.error('No user found in storage.');
        return;
      }

      if (currentUser.user.role_id === ROLEVALUES.SUPER_ADMIN) {
        return; // Skip location update for SUPER_ADMIN
      }

      let latLongOfUser;
      try {
        latLongOfUser = await this.getRequestPermission();
      } catch (err) {
        console.error('Failed to fetch user location:', err);
        return; // Stop further execution if location is not available
      }
      currentUser.user['last_login_location'] = latLongOfUser;
      const updateUser = await this.authService.updateUser(currentUser.user.id, currentUser.user, true);

      if (updateUser) {
        const storageData = this.storageService.getItem('user');
        this.storageService.removeItem('user');
        storageData.user = updateUser;
        this.storageService.setItem('user', storageData);
      } else {
        // console.warn('User update failed, backend did not return updated data.');
      }
    } catch (err) {
      console.error('Error in checkPermission():', err);
    }
  }

  // Add user activity log with current user's details
  async addUserAcitiyLog() {
    try {
      const currentUser = await this.storageService.getItem('user');
      const activityLog = {
        user_id: currentUser.user.id,
        token: currentUser.authToken,
        expired_time: moment(),
      };
      await this.authService.addOrEditUserActivityLog(activityLog);
    } catch (error) {
      console.error('Failed to add user activity log:', error);
    }
  }

  /**
   * @description Reset the file input so the same file can be uploaded again
   * @param fileInput
   */
  resetFileInput(fileInput: HTMLInputElement): void {
    try {
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.log('UtilService ~ resetFileInput ~ error:', error);
    }
  }

  // /**
  //  * @description Get dynamic event name based on Xano environment
  //  * @param prefix
  //  * @param suffix
  //  * @returns Event name
  //  */
  // getDynamicSocketEventName(prefix: string, suffix: string): string {
  //   const baseUrl = environment.XANO_SERVER_URL;
  //   const workspace = XANO_WORKSPACE_CODE;

  //   if (baseUrl.includes(workspace.STAGE_CLONE)) {
  //     return `${prefix}Stage${suffix}`;
  //   } else if (baseUrl.includes(workspace.DEV)) {
  //     return `${prefix}Dev${suffix}`;
  //   } else {
  //     return `${prefix}${suffix}`;
  //   }
  // }

  /**
   * @description
   * Extracts and normalizes the browser origin.
   * Ensures consistent environment detection across the frontend.
   *
   * @returns string - Lowercased window origin
   */
  getFrontendOrigin(): string {
    return window.location.origin.toLowerCase();
  }

  /**
   * @description
   * Detects the active frontend environment from the browser origin.
   * Solves environment-specific socket event segregation on the client side.
   *
   * @returns FrontendSiteType - Detected frontend site type
   */
  detectFrontendSiteType(): FrontendSiteType {
    const origin = this.getFrontendOrigin();

    // Stage environment
    if (origin.includes(FrontendSiteType.STAGE)) {
      return FrontendSiteType.STAGE;
    }

    // Development environment
    if (origin.includes(FrontendSiteType.DEV)) {
      return FrontendSiteType.DEV;
    }

    if (origin.includes(FrontendSiteType.PORTAL)) {
      // Production environment
      return FrontendSiteType.PORTAL;
    }
    return FrontendSiteType.STAGE;
  }

  /**
   * @description
   * Generates a socket event name based on the detected frontend environment.
   * Prevents socket event collisions across Portal, Dev, and Stage deployments.
   *
   * @param prefix - Base socket event prefix (e.g. "send", "calendar")
   * @param suffix - Event action name (e.g. "Message", "Update")
   * @returns string - Environment-aware socket event name
   */
  getDynamicSocketEventName(prefix: string, suffix: string): string {
    const siteType = this.detectFrontendSiteType();

    switch (siteType) {
      case FrontendSiteType.DEV:
        return `${prefix}Dev${suffix}`;

      case FrontendSiteType.STAGE:
        return `${prefix}Stage${suffix}`;

      case FrontendSiteType.PORTAL:
        return `${prefix}${suffix}`;

      default:
        return `${prefix}Stage${suffix}`;
    }
  }

  // /**
  //  * @description Helper function to get the main URL
  //  * @returns string
  //  */
  // getMainUrl(): string {
  //   return environment.XANO_SERVER_URL.includes(XANO_WORKSPACE_CODE.STAGE_CLONE)
  //     ? ENVIRONMENT_URL.Stage
  //     : ENVIRONMENT_URL.Portal;
  // }

  /**
   * @description Helper function to get the main URL
   * Uses frontend origin detection instead of XANO workspace logic.
   *
   * @returns string
   */
  getMainUrl(): string {
    const siteType = this.detectFrontendSiteType();

    return siteType === FrontendSiteType.PORTAL ? ENVIRONMENT_URL.Portal : ENVIRONMENT_URL.Stage;
  }
  /**
   * @description Prevent login for disabled users and display a relevant message
   */
  checkDisabledUser(user: User): boolean {
    if (user && user.isDisable === true) {
      this.toasterService.showToast(ToasterMessages.ACCOUNT_DISABLED_MESSAGE, 'warning');
      return true;
    }
    return false;
  }

  /**
   * Checks if a school has more than one activated calendar.
   *
   * @param {number} schoolId - The ID of the school.
   *
   * @return {Promise<boolean | null>} Returns `true` if there are more than one activated calendar, `false` if one or none, or `null` if an error occurs or response is invalid.
   */
  async checkSchoolCalendarCount(schoolId: number): Promise<boolean | null> {
    try {
      const params = {
        school_id: schoolId,
      };

      const response = await this.calendarService.schoolCalendarBySchoolId(params);
      if (response && response.length > 0) {
        const validSchoolCalendar = response.filter((calendar) => calendar.isActivate === true);
        return validSchoolCalendar.length > 1;
      }
      return false;
    } catch (error) {
      console.error('Error while checking school calendar count:', error);
      return null;
    }
  }

  /**
   * @description Method to render math using MathJax
   * @param opened
   * @returns Promise<void> | undefined
   */
  renderMath(opened: boolean) {
    try {
      if (!opened) return;
      if (opened) {
        if (typeof MathJax !== 'undefined' && MathJax.typesetPromise) {
          return MathJax.typesetPromise();
        } else {
          console.warn('MathJax is not loaded yet');
          return Promise.resolve();
        }
      }
    } catch (error) {
      console.log('UtilService ~ renderMath ~ error:', error);
    }
  }
  /**
   * @description  Method to determine whether to show the default profile image or the user's profile image based on the presence and validity of the media ID and path.
   * @param user
   * @returns boolean
   */

  getProfileImage(user: any): boolean {
    const mediaId = user?.user?.user_profile_media_id || user?.user_profile_media_id;
    const path = user?.user?.user_profile_media?.image?.path || user?.user_profile_media?.image?.path;

    // Show default if mediaId missing or <= 0
    if (!mediaId || mediaId <= 0) {
      return true;
    }

    // Show default if vault path of xano media is missing
    if (mediaId > 0 && path?.includes('vault')) {
      return true;
    }

    return false; // show profile image
  }

  /**
   * @description Method to generate a PDF file name based on the title, type, and whether to include answers.
   * It formats the title by replacing spaces with hyphens, removing unwanted characters, and converting to lowercase, then constructs the file name accordingly.
   * @param title Assignment or assessment title
   * @param type PDF type (assignment or assessment)
   * @param generatePdfWithAnswer Flag indicating whether to include answers in the PDF
   * @returns Formatted PDF file name
   */
  generatePdfFileName(
    title: string,
    type: PdfType.ASSIGNMENT | PdfType.ASSESSMENT,
    generatePdfWithAnswer: boolean,
  ): string {
    const formattedTitle = (title || type)
      .trim()
      .replace(/[^a-zA-Z0-9 _-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 60)
      .toLowerCase();

    return generatePdfWithAnswer
      ? `Respond-EDU-${type}-${formattedTitle}-with-answer`
      : `Respond-EDU-${type}-${formattedTitle}-without-answer`;
  }

  /**
   * @description Formats and validates filename
   * - Trims spaces
   * - Appends .pdf extension
   * - Ensures max length of 60 characters
   *
   * @param name - Input filename
   * @returns formatted filename or null if invalid
   */
  formatFileName(name: string): string | null {
    if (!name?.trim() || name?.trim()?.length > CharacterLimits.FILE_NAME) return null;

    const formatted = name.trim().replace(/\.pdf$/i, '');

    return `${formatted}.pdf`;
  }

  /**
   *  @description Opens dialog for filename confirmation or renaming
   * @param generatePdfWithAnswer - Indicates whether PDF includes answers
   * @param defaultFileName - Pre-generated default filename
   */
  openPdfDialog(
    generatePdfWithAnswer: boolean,
    defaultFileName: string,
    generatePdfCallback: (generatePdfWithAnswer: boolean, fileName: string) => Promise<void>,
  ) {
    const dialogRef = this.dialog.open(RenamePdfDialogComponent, {
      width: '400px',
      data: { defaultFileName, generatePdfWithAnswer },
      disableClose: true,
      panelClass: 'custom-rename-dialog',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      // If user clicks on cancel button, then navigate back
      if (!result || result.isCancel === true) return;

      const finalFileName = this.formatFileName(result.fileName);

      if (!finalFileName) {
        this.toasterService.showToast('Please provide a valid file name.', 'danger');
        return;
      }

      // Call passed function
      try {
        await generatePdfCallback(generatePdfWithAnswer, finalFileName);
      } catch (error) {
        console.error(error);
        this.toasterService.showToast('An error occurred while generating the PDF.', 'danger');
      }
    });
  }

  /**
   * @description Prepares filename and opens rename dialog for PDF generation
   * @param title - Assignment or Assessment title
   * @param type - Pdf type (assignment / assessment)
   * @param generatePdfWithAnswer - Indicates whether PDF includes answers
   */
  async prepareAndDownloadPdf(
    title: string,
    type: PdfType,
    generatePdfWithAnswer: boolean,
    generatePdfCallback: (generatePdfWithAnswer: boolean, fileName: string) => Promise<void>,
  ) {
    try {
      const finalTitle = title || type;

      const defaultFileName = this.generatePdfFileName(finalTitle, type, generatePdfWithAnswer);

      this.openPdfDialog(generatePdfWithAnswer, defaultFileName, generatePdfCallback);
    } catch (error) {
      console.error(error);
      this.toasterService.showToast('An error occurred while generating the PDF.', 'danger');
    }
  }

  /**
   * @description Validates the provided file name.
   * @param name - The file name to validate.
   * @returns An object indicating whether the file name is valid and any associated error message.
   */
  validateFileName(name: string): { valid: boolean; error?: string } {
    if (!name) {
      return { valid: false, error: 'Please enter a file name.' };
    }

    const trimmed = name.trim();

    if (!trimmed) {
      return { valid: false, error: 'Please enter a file name.' };
    }

    if (trimmed.length > CharacterLimits.FILE_NAME) {
      return {
        valid: false,
        error: `File name cannot exceed ${CharacterLimits.FILE_NAME} characters.`,
      };
    }

    if (/\s{2,}/.test(trimmed)) {
      return {
        valid: false,
        error: 'Only a single space is allowed between characters.',
      };
    }

    const regex = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+( [a-zA-Z0-9_-]+)*$/;

    if (!regex.test(trimmed)) {
      return {
        valid: false,
        error:
          'File name must contain at least one letter or number and can include letters, numbers, hyphens (-), underscores (_), and single spaces.',
      };
    }

    return { valid: true };
  }
  // Method to validate school code input and remove whitespace
  validateSchoolByCode(event: any, control: any) {
    try {
      const inputValue = event.target.value;
      const cleanedValue = this.cleanWhitespace(inputValue);

      // Only update if actually different
      if (inputValue !== cleanedValue) {
        control.patchValue(cleanedValue, { emitEvent: false });
      }
    } catch (error) {
      console.error(error);
    }
  }
  // Utility function to remove all whitespace from a string  start,end, middle
  cleanWhitespace(value: string): string {
    return value ? value.replace(/\s+/g, '') : value;
  }

  /**
   * @description Fetches an image from a given URL and converts it into a Base64
   * encoded string.
   * @param url - Publicly accessible image URL
   * @returns A Promise that resolves to:
   * - Base64 string if conversion is successful
   * - null if the URL is invalid, request fails, or conversion errors occur
   */
  async getBase64FromUrl(url: string): Promise<string | null> {
    if (!url) return null;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const blob = await response.blob();

      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.log('UtilService ~ getBase64FromUrl ~ error:', error);
      return null;
    }
  }
}
